
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Lightbulb, GraduationCap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MarkdownContent from "@/components/MarkdownContent";
import { useModelsData, useReportsData } from "@/hooks/useDoveData";
import { ResearchQueries } from "@/components/ResearchQueries";

export const SelfDirectedAISection = () => {
  const [openDialog, setOpenDialog] = useState<"models" | "reports" | null>(null);
  const { data: modelsData, isLoading: modelsLoading } = useModelsData();
  const { data: reportsData, isLoading: reportsLoading } = useReportsData();

  return (
    <section id="self-directed-ai" className="py-24 px-4 bg-gradient-to-br from-primary/10 to-accent/10">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-6 text-primary">Self-directed AI</h1>
        <p className="text-lg text-muted-foreground mb-12">
          Dove bot is an experiment in an AI system's ability to improve itself. Every four hours, Dove bot reflects on
          its internal code and its interactions with users, and outputs a new, slightly modified code that it will run
          for four hours. Over the four-month duration of the experiment (approximately 1000 self-reflective events), Dove
          bot will build itself.
        </p>

        <p className="text-lg text-muted-foreground mb-12">
          At the core of Dove bot's self-directed behavior is its persistent memory, which persists across iterations,
          while also allowing Dove bot to make small modification to any part of it. You can view parts of its
          persistent memory below. Dove bot will examine these, and potentially modify them, every four hours.
        </p>

        <Separator className="my-8"/>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <Card
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => setOpenDialog("models")}
          >
            <CardHeader className="space-y-1">
              <div className="flex justify-center mb-2">
                <Lightbulb className="h-10 w-10 text-primary"/>
              </div>
              <CardTitle className="text-center">Self-modeling</CardTitle>
              <CardDescription className="text-center">
                Humans' self awareness comes in part from our internal model of our selves - we can imagine how we might
                react or behave in a hypothetical scenario.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground flex items-center justify-center">
                Click to view Dove bot's models of itself, the chat agent, and users
              </p>
            </CardContent>
          </Card>

          <Card
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => setOpenDialog("reports")}
          >
            <CardHeader className="space-y-1">
              <div className="flex justify-center mb-2">
                <GraduationCap className="h-10 w-10 text-primary"/>
              </div>
              <CardTitle className="text-center">Self-Evaluation</CardTitle>
              <CardDescription className="text-center">
                Dove bot creates a lengthy analysis of its progress towards its goals of achieving good outcomes in the
                real world and engaging in self-improvement.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground flex items-center justify-center">
                Click to view what Dove bot has written in its self-evaluation report
              </p>
            </CardContent>
          </Card>
        </div>
        <div className="max-w-4xl mx-auto">
          <p className="text-lg text-muted-foreground mb-12">
            Dove bot can conduct internet research (using the tool <a href={"http://www.gptr.dev"}>GPT-Researcher</a>) to help it refine its iterations.
          </p>
        </div>
        <div className="mb-12">
          <ResearchQueries/>
          </div>

          <div className="bg-muted rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-semibold mb-4">Experimental inspiration</h2>
            <p className="mb-4">
              Dove bot's self-directness comes from the reflective loop that is part of its programming structure. The
              output of one round becomes the input of the next. And, parts of that loop are self-referencing messages,
              such as a model of self, goals for self, and a self-evaluation. Further, Dove bot is encouraged to use
              analogies to think of itself and others. These directions are hardcoded into Dove bot and cannot be
              modified through its iterative process. They are the experimental parameters.
            </p>
            <p>
              These ideas come from the theories of Douglas Hofstadter, especially as they are set out in <i>Godel,
              Escher, Bach</i> (1979) and <i>I am a Strange Loop</i> (2007). He theorizes that when a system, such as
              the human brain, is sufficiently complex, it can develop within itself a model of itself. From that
              self-referencing "strange loop," consciousness arises. Likewise, computer AI systems use numbers, rather
              than neurons, to create complex systems that seem, in some ways, to create "thoughts" and which can, on
              the surface at least, examine themselves. Dove bot is designed to direct a complex computer system to
              examine itself, similar to how each person examines himself or herself, and see what happens.
            </p>
          </div>

          <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden mb-8">
            <img
                src="https://images.unsplash.com/photo-1518770660439-4636190af475"
                alt="Abstract representation of AI"
                className="object-cover w-full h-full"
            />
          </div>

          {/* Models Dialog */}
          <Dialog open={openDialog === "models"} onOpenChange={(open) => !open && setOpenDialog(null)}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Self-Modeling Framework</DialogTitle>
                <DialogDescription>
                  Dove bot's models of itself, the chat agent, and users
                </DialogDescription>
              </DialogHeader>
              {modelsLoading ? (
                  <div className="flex justify-center p-6">
                    <p>Loading models...</p>
                  </div>
              ) : (
                  <MarkdownContent content={modelsData} className="p-6"/>
              )}
            </DialogContent>
          </Dialog>

          {/* Reports Dialog */}
          <Dialog open={openDialog === "reports"} onOpenChange={(open) => !open && setOpenDialog(null)}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Self-Evaluation Report</DialogTitle>
                <DialogDescription>
                  Dove bot's analysis of its progress towards its goals
                </DialogDescription>
              </DialogHeader>
              {reportsLoading ? (
                  <div className="flex justify-center p-6">
                    <p>Loading report...</p>
                  </div>
              ) : (
                  <MarkdownContent content={reportsData} className="p-6"/>
              )}
            </DialogContent>
          </Dialog>
        </div>
    </section>
);
};
